import React, { useEffect, useState } from 'react';
import { Crown, Flag } from 'lucide-react';
import { nomeParaCena } from 'features/comercial/cenas/eventosDoRanking';

/**
 * A corrida de hoje: uma raia por vendedor, e o kart anda conforme as vendas do dia.
 *
 * Fica embaixo do ranking do período para a TV mostrar as duas coisas sem ninguém clicar: quem
 * lidera o mês e quem está vendendo AGORA. As raias chegam em ordem fixa (`corredoresDeHoje`) e não
 * trocam de lugar quando alguém passa: a ultrapassagem acontece na pista, e o selo da esquerda diz
 * a posição.
 *
 * Com meta do dia, a pista tem duas metas. Enquanto ninguém bate a meta do dia, ela é o fim da
 * pista. Quando alguém bate, a linha da meta vai para 90% e o trecho final leva à bandeira dourada
 * da supermeta do dia: quem bateu a meta fica À FRENTE da linha, e quem bate a supermeta encosta na
 * bandeira. Nesse instante os outros karts recuam junto com a linha — decisão de 03/11/2026.
 *
 * Sem meta do dia, a referência é o líder, que fica a 90% da pista: ali a bandeira não é meta de
 * ninguém, e um kart encostado nela leria como "acabou".
 */

/** Onde fica a linha da meta do dia: no fim da pista, até alguém batê-la; depois, a 90%. */
export const linhaDaMeta = (raias, meta) => (meta > 0 && raias.some((raia) => raia.hoje >= meta) ? 0.9 : 1);

/**
 * Fração da pista percorrida, de 0 (largada) a 1 (encostado na bandeira).
 *
 * Quem bate a meta já conta um passo além da linha, para não ficar em cima dela. Sem supermeta, o
 * trecho depois da linha mede mais uma meta.
 */
export const avancoDoKart = (hoje, { meta = 0, supermeta = 0, maior = 0, linha = 1 } = {}) => {
  if (!(meta > 0)) return maior > 0 ? Math.min(hoje / maior, 1) * 0.9 : 0;
  if (hoje < meta) return (hoje / meta) * linha;
  if (linha >= 1) return 1;
  const trechoFinal = (supermeta > meta ? supermeta - meta : meta) + 1;
  return linha + (1 - linha) * Math.min((hoje - meta + 1) / trechoFinal, 1);
};

/**
 * A largura do kart na pista, e o x da linha da meta: onde para o bico de um kart a 90%. Em pista
 * estreita, 10% não comportam um kart depois da linha: ela recua até sobrar meio kart de trecho final.
 */
const KART = '76px * var(--kart, 0.88)';
const xDaLinha = (linha) => `min((100% - ${KART}) * ${linha} + ${KART}, 100% - 1.5 * ${KART} - 6px)`;

/**
 * O `left` do kart. Antes da linha, a fração da pista. Depois dela, a posição conta a partir da
 * linha, em pixels: em fração, a largura do kart engolia o trecho final e quem bateu a meta ficava
 * em cima da linha, e não à frente. Logo depois da linha ao bater a meta; na bandeira, a supermeta.
 */
const leftDoKart = (avanco, linha, passou) => {
  if (linha >= 1) return `calc((100% - ${KART}) * ${avanco})`;
  // Antes da linha, o bico para nela — mesmo quando a pista estreita a puxou para trás.
  if (!passou) return `min(calc((100% - ${KART}) * ${avanco}), calc(${xDaLinha(linha)} - ${KART}))`;
  const t = (avanco - linha) / (1 - linha);
  return `calc(${xDaLinha(linha)} + 6px + ((100% - ${KART}) - (${xDaLinha(linha)} + 6px)) * ${t})`;
};

const numero = (valor) => Number(valor || 0).toLocaleString('pt-BR');
const primeiroNome = (nome = '') => String(nome).trim().split(/\s+/)[0] || '';
const iniciais = (nome = '') => String(nome).trim().split(/\s+/).filter(Boolean).slice(0, 2)
  .map((parte) => parte[0]).join('').toUpperCase();

const classeDaPosicao = (posicao, comVenda) => {
  if (!comVenda) return 'bg-white/[0.14] text-white/70';
  if (posicao === 1) return 'bg-amber-300 text-slate-950 shadow-[0_0_14px_rgba(251,191,36,0.4)]';
  if (posicao === 2) return 'bg-slate-300 text-slate-900';
  if (posicao === 3) return 'bg-amber-700/80 text-white';

  return 'bg-white text-slate-950';
};

const Kart = ({ vendedor, lider, andando }) => (
  <span className={`corrida-kart ${andando ? 'corrida-kart-andando' : ''}`}>
    <svg viewBox="-14 0 90 30" aria-hidden="true" className="absolute bottom-0 left-[-14px] h-[30px] w-[90px] overflow-visible">
      <path d="M4 14h10l6-6h30l10 8h8a4 4 0 0 1 4 4v3H4z" fill={lider ? '#F2C14E' : '#38bdf8'} />
      <rect x="2" y="9" width="8" height="4" rx="1" fill="#e2e8f0" />
      <circle className="corrida-roda" cx="18" cy="23" r="6.5" fill="#020617" stroke="#e2e8f0" strokeWidth="2" strokeDasharray="4 3" />
      <circle className="corrida-roda" cx="58" cy="23" r="6.5" fill="#020617" stroke="#e2e8f0" strokeWidth="2" strokeDasharray="4 3" />
    </svg>
    {vendedor.foto_url ? (
      <img src={vendedor.foto_url} alt="" className="absolute left-[22px] top-0 h-[30px] w-[30px] rounded-full object-cover ring-2 ring-slate-950" />
    ) : (
      <span className="absolute left-[22px] top-0 grid h-[30px] w-[30px] place-items-center rounded-full bg-[linear-gradient(135deg,var(--accent),rgba(250,204,21,0.88))] text-[11px] font-black text-white ring-2 ring-slate-950">
        {iniciais(vendedor.nome_exibicao || vendedor.nome)}
      </span>
    )}
    {lider && <Crown aria-hidden="true" className="absolute left-[27px] top-[-15px] h-5 w-5 fill-amber-300 text-amber-300 drop-shadow-[0_2px_4px_rgba(2,6,23,0.7)]" />}
  </span>
);

/**
 * `estado` diz de quando são as vendas do dia: 'ok', 'carregando' (ainda não chegaram),
 * 'desatualizado' (a última consulta falhou e o que está na pista é de antes) ou 'indisponivel'
 * (nunca chegaram). Fora do 'ok' a pista diz isso por escrito e o ponto para de piscar — karts
 * parados no zero sem aviso leriam como "ninguém vendeu".
 *
 * `compacto` é a TV: tudo em 28 px para se ler do outro lado da sala.
 */
const AVISOS_DO_ESTADO = {
  carregando: 'carregando as vendas de hoje…',
  desatualizado: 'vendas de hoje sem atualizar',
  indisponivel: 'vendas de hoje indisponíveis',
};

/**
 * A altura da raia e o tamanho do kart na TV, conforme quantas raias há.
 *
 * Com quatro raias sobra altura, e o kart de 30 px ficava perdido numa pista de 1.400: raia e kart
 * crescem. Com seis, a pista inteira tem de caber embaixo do pódio, e os dois voltam ao tamanho
 * de mesa.
 */
const medidasDaPista = (raias) => {
  if (raias <= 4) return { '--raia': '60px', '--kart': 1.25 };
  if (raias === 5) return { '--raia': '52px', '--kart': 1.1 };

  return { '--raia': '46px', '--kart': 0.95 };
};

const TEMPO_DO_MAIS_UM_MS = 2600;
/** Quanto dura a comemoração de quem bateu a meta ou a supermeta do dia. */
const TEMPO_DA_META_DO_DIA_MS = 6000;
const SELO_DA_META = { meta: 'Meta do dia!', supermeta: 'Supermeta do dia!' };

const CorridaDeHoje = ({ raias = [], somenteInternos = false, dia = '', compacto = false, estado = 'ok', vendasNovas = null, metaChegada = 0, supermetaChegada = 0, metasDoDia = null, metaIndisponivel = false }) => {
  const semDado = estado === 'desatualizado' || estado === 'indisponivel';
  const nomeDaRaia = (raia) => primeiroNome(compacto ? nomeParaCena(raia.nome_exibicao || raia.nome) : (raia.nome_exibicao || raia.nome));
  const maior = Math.max(...raias.map((raia) => raia.hoje), 0);
  const linha = linhaDaMeta(raias, metaChegada);
  const comSupermeta = metaChegada > 0 && supermetaChegada > metaChegada;
  const metasDoSubtitulo = metaChegada > 0
    ? ` · Meta do dia: ${numero(metaChegada)}${comSupermeta ? ` · Supermeta: ${numero(supermetaChegada)}` : ''}`
    : '';
  const subtitulo = `${somenteInternos ? 'Vendedores internos' : 'Quem vendeu hoje'}${dia ? ` · ${dia}` : ''}${metasDoSubtitulo}${metaIndisponivel ? ' · Meta diária indisponível' : ''}${AVISOS_DO_ESTADO[estado] ? ` · ${AVISOS_DO_ESTADO[estado]}` : ''}`;
  const pontoAoVivo = (
    <span
      className={`inline-block h-2 w-2 shrink-0 rounded-full ${semDado ? 'bg-amber-400' : 'bg-emerald-400'}`}
      style={semDado ? undefined : { animation: 'live-blink 1.6s ease-in-out infinite 0.8s' }}
      role="img"
      aria-label={semDado ? 'Dados desatualizados' : 'Dados ao vivo'}
    />
  );
  // Quem acabou de vender: o kart gira as rodas e ganha um "+1" em cima por alguns segundos. Quem
  // diz QUEM vendeu é o painel, que compara dois ciclos — a pista não tenta descobrir sozinha, porque
  // a primeira carga do dia (todo mundo saindo do zero) pareceria uma venda de cada um.
  const [emDestaque, setEmDestaque] = useState(null);

  useEffect(() => {
    if (!vendasNovas || !vendasNovas.marca) return undefined;

    setEmDestaque(vendasNovas.porId);
    const fim = setTimeout(() => setEmDestaque(null), TEMPO_DO_MAIS_UM_MS);

    return () => clearTimeout(fim);
  }, [vendasNovas]);

  // Quem cruzou a meta ou a supermeta do dia. Como o "+1", quem decide é o painel, que compara
  // dois ciclos: a pista só comemora.
  const [comemorando, setComemorando] = useState(null);

  useEffect(() => {
    if (!metasDoDia || !metasDoDia.marca) return undefined;

    setComemorando(metasDoDia.porId);
    const fim = setTimeout(() => setComemorando(null), TEMPO_DA_META_DO_DIA_MS);

    return () => clearTimeout(fim);
  }, [metasDoDia]);
  const linhaPulsa = Boolean(comemorando) && Object.values(comemorando).includes('meta');

  return (
    <section
      className={`corrida-de-hoje mt-5 shrink-0 ${compacto ? '' : 'rounded-3xl border border-white/[0.14] bg-slate-950/[0.44] p-4 backdrop-blur-xl sm:p-6'}`}
      style={compacto ? medidasDaPista(raias.length) : undefined}
      aria-label="Corrida de hoje"
    >
      {compacto ? (
        // Na TV o bloco não tem painel em volta, como o ranking acima dele: as raias já são a forma.
        // `space-x`, e não `gap`: em flex o `gap` só existe do Chromium 84 em diante, e no navegador
        // da TV os itens ficavam colados.
        <div className="mb-3 flex items-center space-x-3">
          <Flag className="h-7 w-7 shrink-0 text-amber-300" aria-hidden="true" />
          <h3 className="text-2xl font-black text-white">Corrida de hoje</h3>
          {pontoAoVivo}
          <span className="h-px min-w-0 flex-1 bg-white/[0.14]" aria-hidden="true" />
          <p className={`shrink-0 text-xl font-medium ${semDado ? 'text-amber-300' : 'text-white/70'}`}>{subtitulo}</p>
        </div>
      ) : (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-white/10 text-[color:var(--accent)]">
              <Flag className="h-5 w-5" aria-hidden="true" />
            </span>
            <h3 className="text-lg font-black text-white">Corrida de hoje</h3>
            {pontoAoVivo}
          </div>
          <p className={`text-xs font-semibold ${semDado ? 'text-amber-300' : 'text-white/70'}`}>{subtitulo}</p>
        </div>
      )}

      {raias.length === 0 ? (
        <p className={`py-6 text-center text-white/70 ${compacto ? 'text-2xl font-semibold' : 'text-sm'}`}>
          {estado === 'carregando'
            ? 'Carregando as vendas de hoje…'
            : estado === 'indisponivel'
              ? 'Não foi possível carregar as vendas de hoje.'
              : 'Ninguém largou ainda hoje. O primeiro kart aparece na primeira venda do dia.'}
        </p>
      ) : (
        <ol className={`grid ${compacto ? 'gap-2' : 'gap-1.5'}`}>
          {raias.map((raia) => {
            const comVenda = raia.hoje > 0;
            const lider = comVenda && raia.posicao === 1;
            const avanco = avancoDoKart(raia.hoje, { meta: metaChegada, supermeta: supermetaChegada, maior, linha });
            const festa = comemorando ? comemorando[String(raia.id)] : null;
            const bateu = metaChegada > 0 && raia.hoje >= metaChegada;
            const bateuSuper = comSupermeta && raia.hoje >= supermetaChegada;

            return (
              <li
                key={raia.id}
                className={`grid items-center ${compacto ? 'grid-cols-[minmax(0,250px)_minmax(0,1fr)_72px] gap-4' : 'grid-cols-[minmax(0,112px)_minmax(0,1fr)_36px] gap-3 sm:grid-cols-[minmax(0,160px)_minmax(0,1fr)_44px]'}`}
              >
                <div className={`flex min-w-0 items-center ${compacto ? 'space-x-3.5' : 'space-x-2.5'}`}>
                  <span className={`grid shrink-0 place-items-center font-black tabular-nums ${compacto ? 'h-10 w-10 rounded-xl text-xl' : 'h-8 w-8 rounded-[10px] text-[13px]'} ${classeDaPosicao(raia.posicao, comVenda)}`}>
                    {comVenda ? raia.posicao : '–'}
                  </span>
                  <span className={`truncate font-black text-white ${compacto ? 'text-[28px] leading-none' : 'text-sm'}`} title={raia.nome}>
                    {nomeDaRaia(raia)}
                  </span>
                </div>
                <div className={`corrida-asfalto ${linha < 1 ? (comSupermeta ? 'corrida-asfalto-super' : 'corrida-asfalto-aberta') : ''}`}>
                  <div className="absolute inset-y-0 left-0 right-5">
                    {linha < 1 && (
                      <span
                        className={`corrida-linha ${linhaPulsa ? 'corrida-linha-pulsa' : ''}`}
                        style={{ left: `calc(${xDaLinha(linha)})` }}
                        aria-hidden="true"
                      />
                    )}
                    <div
                      className="absolute top-1/2 -mt-[23px] transition-[left] duration-[900ms] ease-out"
                      style={{ left: leftDoKart(avanco, linha, bateu) }}
                    >
                      {festa && (
                        <span className={`corrida-selo corrida-selo-${festa}`} style={avanco > 0.5 ? { left: 'auto', right: 0 } : undefined} role="status">
                          <Flag className="h-[0.9em] w-[0.9em] shrink-0" aria-hidden="true" />
                          {SELO_DA_META[festa]}
                          <span className="sr-only">{` ${raia.nome_exibicao || raia.nome}`}</span>
                        </span>
                      )}
                      <span className={festa ? `corrida-kart-festa corrida-kart-festa-${festa}` : undefined}>
                        <Kart vendedor={raia} lider={lider} andando={Boolean(emDestaque && emDestaque[String(raia.id)])} />
                      </span>
                      {emDestaque && emDestaque[String(raia.id)] > 0 && (
                        <span className="corrida-mais" aria-hidden="true">+{emDestaque[String(raia.id)]}</span>
                      )}
                    </div>
                  </div>
                </div>
                <span className={`text-right font-black tabular-nums ${bateuSuper ? 'text-amber-300' : bateu ? 'text-emerald-300' : 'text-white'} ${compacto ? 'text-[32px] leading-none' : 'text-lg'}`}>
                  <span className="sr-only">{`${nomeDaRaia(raia)}: `}</span>
                  {numero(raia.hoje)}
                  <span className="sr-only"> vendas hoje</span>
                </span>
              </li>
            );
          })}
        </ol>
      )}

      <style>{`
        .corrida-asfalto {
          position: relative; height: var(--raia, 46px); border-radius: 12px;
          border: 1px solid rgba(255,255,255,0.08); background-color: #111a33;
          background-image: repeating-linear-gradient(90deg, rgba(255,255,255,0.16) 0 22px, transparent 22px 54px);
          background-size: 100% 2px; background-repeat: no-repeat; background-position: 0 50%;
        }
        .corrida-asfalto::after {
          content: ""; position: absolute; right: 0; top: 0; bottom: 0; width: 20px; opacity: 0.85;
          border-radius: 0 11px 11px 0;
          background: repeating-conic-gradient(#fff 0 25%, #020617 0 50%) 0 0 / 10px 10px;
        }
        .corrida-asfalto-super::after { background: repeating-linear-gradient(180deg, #F2C14E 0 6px, #b7791f 6px 12px); opacity: 0.95; }
        .corrida-asfalto-aberta::after { display: none; }
        .corrida-linha {
          position: absolute; top: 0; bottom: 0; width: 8px; margin-left: 2px; opacity: 0.9;
          background: repeating-conic-gradient(#fff 0 25%, #020617 0 50%) 0 0 / 8px 8px;
          transition: left 900ms ease-out;
        }
        .corrida-linha-pulsa { animation: corrida-linha-pulsa 0.6s ease-in-out 5; }
        @keyframes corrida-linha-pulsa { 50% { opacity: 1; box-shadow: 0 0 18px 4px rgba(110,231,183,0.75); } }
        .corrida-kart-festa { display: block; border-radius: 999px; }
        .corrida-kart-festa-meta { animation: corrida-festa-meta 0.8s ease-out 4; }
        .corrida-kart-festa-supermeta { animation: corrida-festa-super 0.7s ease-out 6; }
        @keyframes corrida-festa-meta {
          0% { filter: drop-shadow(0 0 0 rgba(110,231,183,0)); transform: translateY(0); }
          40% { filter: drop-shadow(0 0 12px rgba(110,231,183,0.95)); transform: translateY(-5px); }
          100% { filter: drop-shadow(0 0 0 rgba(110,231,183,0)); transform: translateY(0); }
        }
        @keyframes corrida-festa-super {
          0% { filter: drop-shadow(0 0 0 rgba(242,193,78,0)); transform: translateY(0) scale(1); }
          40% { filter: drop-shadow(0 0 16px rgba(242,193,78,1)); transform: translateY(-7px) scale(1.06); }
          100% { filter: drop-shadow(0 0 0 rgba(242,193,78,0)); transform: translateY(0) scale(1); }
        }
        .corrida-selo {
          position: absolute; left: 0; bottom: calc(100% + 4px); display: inline-flex; align-items: center;
          padding: 2px 10px; border-radius: 999px; font-weight: 900; font-size: calc(15px * var(--kart, 0.88) / 0.88);
          line-height: 1.3; white-space: nowrap; box-shadow: 0 4px 12px rgba(2,6,23,0.55);
          animation: corrida-selo ${TEMPO_DA_META_DO_DIA_MS}ms ease-out both;
        }
        .corrida-selo > svg { margin-right: 4px; }
        .corrida-selo-meta { background: #6ee7b7; color: #022c22; }
        .corrida-selo-supermeta { background: #F2C14E; color: #020617; }
        @keyframes corrida-selo {
          0% { opacity: 0; transform: translateY(8px) scale(0.6); }
          6% { opacity: 1; transform: translateY(-3px) scale(1.1); }
          12% { transform: translateY(0) scale(1); }
          88% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-10px); }
        }
        @media (prefers-reduced-motion: reduce) {
          .corrida-kart-festa-meta, .corrida-kart-festa-supermeta, .corrida-linha-pulsa { animation: none; }
          .corrida-selo { animation: none; }
        }
        .corrida-kart { position: relative; display: block; width: 76px; height: 46px; transform: scale(var(--kart, 0.88)); transform-origin: left center; }
        .corrida-roda { transform-box: fill-box; transform-origin: center; }
        .corrida-kart-andando .corrida-roda { animation: corrida-gira 0.25s linear 4; }
        .corrida-kart-andando svg { animation: corrida-quica 0.22s ease-in-out 4; }
        @keyframes corrida-gira { to { transform: rotate(360deg); } }
        @keyframes corrida-quica { 50% { transform: translateY(-2px); } }
        .corrida-mais {
          position: absolute; left: 62px; top: -16px; padding: 1px 10px; border-radius: 999px;
          background: #F2C14E; color: #020617; font-weight: 900; font-size: 20px; line-height: 1.3;
          white-space: nowrap; box-shadow: 0 4px 12px rgba(2,6,23,0.55);
          animation: corrida-mais 2.6s ease-out both;
        }
        @keyframes corrida-mais {
          0% { opacity: 0; transform: translateY(8px) scale(0.6); }
          12% { opacity: 1; transform: translateY(-4px) scale(1.12); }
          22% { transform: translateY(-6px) scale(1); }
          80% { opacity: 1; transform: translateY(-10px) scale(1); }
          100% { opacity: 0; transform: translateY(-22px) scale(1); }
        }
      `}</style>
    </section>
  );
};

export default CorridaDeHoje;
